/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";
import { withSessionToken } from "@/utils/sessionToken";

export interface RouterStatus {
  id: string;
  name: string;
  sshHost: string;
  sshPort: number;
  sshUser: string;
  profile: RouterProfile;
  online: boolean;
  youtubeOk: boolean;
  servicesOk: boolean;
  dnsAdblockOk: boolean;
  dnsInternetOk: boolean;
  hostname?: string;
  uptime?: string;
  overlay?: string;
  memory?: string;
  youtubeLine?: string;
  dnsLine?: string;
  model?: string;
  kernel?: string;
  openwrt?: string;
  load1?: string;
  load5?: string;
  load15?: string;
  uptimeSec?: number;
  memoryStats?: RouterMemoryStats;
  storageStats?: RouterStorageStats;
  interfaces?: RouterInterfaceStats[];
  wifiRadios?: RouterWifiRadio[];
  cpu?: RouterCPUStats;
  wifiClients?: RouterWifiClient[];
  conntrack?: RouterConntrackStats;
  dnsMode?: string;
  defaultRoute?: string;
  latency?: RouterLatencyCheck[];
  logHealth?: RouterLogHealth;
  watchdog?: RouterWatchdogStatus;
  error?: string;
  checkedAt: string;
  rawOutput?: string;
}

export interface RouterProfile {
  displayName?: string;
  location?: string;
  owner?: string;
  purpose?: string;
  notes?: string;
}

export interface RouterMemoryStats {
  totalKb: number;
  usedKb: number;
  freeKb: number;
  availableKb: number;
  cachedKb: number;
  usedPercent: number;
}

export interface RouterStorageStats {
  filesystem: string;
  size: string;
  used: string;
  available: string;
  usePercent: number;
  mountpoint: string;
}

export interface RouterInterfaceStats {
  name: string;
  rxBytes: number;
  rxPackets: number;
  rxErrors: number;
  rxDrops: number;
  txBytes: number;
  txPackets: number;
  txErrors: number;
  txDrops: number;
}

export interface RouterWifiRadio {
  phy?: string;
  interface?: string;
  ssid?: string;
  channel?: string;
  width?: string;
  frequency?: string;
  txPower?: string;
  type?: string;
}

export interface RouterCPUStats {
  userPercent: number;
  systemPercent: number;
  idlePercent: number;
  busyPercent: number;
}

export interface RouterWifiClient {
  interface: string;
  mac: string;
  signalDbm?: number;
  rxRate?: string;
  txRate?: string;
  expectedThroughput?: string;
  inactive?: string;
}

export interface RouterConntrackStats {
  count: number;
  max: number;
  usePercent: number;
}

export interface RouterLatencyCheck {
  name: string;
  target: string;
  ok: boolean;
  timeMs?: string;
  line?: string;
}

export interface RouterLogHealth {
  issueCount: number;
  recent?: string[];
}

export interface RouterWatchdogStatus {
  dnsInstalled: boolean;
  cronRunning: boolean;
  lastRun?: string;
}

export interface RouterActionResponse {
  id: string;
  name: string;
  success: boolean;
  output: string;
  checkedAt: string;
}

export interface RouterCheckEvent {
  group: "ssh" | "youtube" | "services" | "done";
  name: string;
  target?: string;
  status: "running" | "ok" | "error" | "done";
  ok: boolean;
  detail?: string;
  checkedAt: string;
}

async function parseErrorOr(response: Response, fallback: string): Promise<never> {
  const errorData = await response.json().catch(() => ({}));
  throw new Error(errorData.error || errorData.output || fallback);
}

export async function getRouters(): Promise<RouterStatus[]> {
  const response = await fetch(getApiUrl("/routers"));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch routers");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function getRouter(id: string): Promise<RouterStatus> {
  const response = await fetch(getApiUrl(`/routers/${id}`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch router");
  return response.json();
}

export async function fixRouterYouTube(id: string): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/fix-youtube`), {
    method: "POST",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to fix YouTube");
  return response.json();
}

export async function backupRouter(id: string): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/backup`), {
    method: "POST",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to backup router");
  return response.json();
}

export async function checkRouterDNS(id: string): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/dns`));
  if (!response.ok) return parseErrorOr(response, "Failed to check router DNS");
  return response.json();
}

export async function installRouterDNSWatchdog(id: string): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/dns-watchdog`), {
    method: "POST",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to install DNS watchdog");
  return response.json();
}

export async function restartRouterService(id: string, service: "zapret" | "dns" | "ssh-tunnel"): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/restart/${service}`), {
    method: "POST",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to restart router service");
  return response.json();
}

export async function updateRouterProfile(id: string, profile: RouterProfile): Promise<RouterStatus> {
  const response = await fetch(getApiUrl(`/routers/${id}/profile`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(profile),
  });
  if (!response.ok) return parseErrorOr(response, "Failed to update router profile");
  return response.json();
}

export function getRouterCheckStreamUrl(id: string): string {
  return withSessionToken(getApiUrl(`/routers/${id}/check-stream`));
}
